// ABOUTME: The two caption reveal paces — the agent's, driven by its voice playback clock, and the
// ABOUTME: guest's, a short catch-up smoothing. Both share one monotonic word-boundary cursor.

import { useEffect, useRef, useState } from 'react';
import { speechProgress } from '../../../state/speechClock';

// WHY THE TWO SENDERS ARE PACED DIFFERENTLY. The agent's transcript arrives BEFORE the voice that
// speaks it — Gemini emits `output_transcription` far faster than the audio queue can play it — so
// its caption has to be held back to the audio clock or it reaches the last sentence of an answer
// the guest has not heard the start of.
//
// The guest's transcript is the opposite: it is ASR of words they have ALREADY said (the browser
// does no speech recognition at all — it streams PCM, and `input_transcription` comes back from the
// backend). There is no future audio to pace against, and any deliberate delay would put their own
// words further behind them. What it needs is only smoothing: interim chunks land in bursts of a
// few words, so a raw render clunks. A fast catch-up makes that read as a live subtitle track
// without adding meaningful latency — the cursor is fully caught up inside ~200ms.

const VOICE_TICK_MS = 80;   // the agent: the audio clock moves slowly and predictably
const SMOOTH_TICK_MS = 40;  // the guest: short ticks so the catch-up reads as typing, not stepping
// Fraction of the remaining gap closed per tick. 0.5 at 40ms clears a typical ASR chunk in ~4 ticks.
const CATCH_UP = 0.5;

/**
 * Reveal the AGENT's transcript in step with the voice playing it.
 *
 * Two properties matter more than the pacing itself: it never rewinds within a turn, and it always
 * finishes — when the audio queue drains, progress is 1 and the whole line is on screen, so an
 * under- or over-estimate mid-answer cannot leave the caption permanently truncated.
 *
 * With no audio clock (a text-only turn, playback not started, tests) `speechProgress()` returns
 * null and this is a pass-through — the pre-pacing behaviour, unchanged.
 */
export function useVoicePacedReveal(text) {
  return useRevealCursor(text, voicePacedTarget, VOICE_TICK_MS);
}

/**
 * Reveal the GUEST's transcript with a short catch-up, so a burst of ASR text slides in instead of
 * appearing as a block. Converges on whatever has arrived, so it can never fall behind the socket.
 */
export function useSmoothReveal(text) {
  return useRevealCursor(text, smoothTarget, SMOOTH_TICK_MS);
}

// --- the shared cursor ------------------------------------------------------
//
// The backend sends BOTH transcripts cumulatively (`unsaved_agent_text` / `unsaved_user_text` in
// `stream_controller._downstream_task`), so the text only ever grows within a turn — which is what
// makes a single "how many characters are visible" cursor the whole of the state here.
//
// `nextTarget` must be a module-level function, not an inline arrow: it is an effect dependency,
// and a fresh identity every render would tear the interval down and rebuild it on every frame.

function useRevealCursor(text, nextTarget, tickMs) {
  const full = text || '';
  // Seeded from `nextTarget` rather than from `full.length`: the cursor only moves forward, so
  // starting it at the end of the text would pin it there for the whole first utterance.
  const [len, setLen] = useState(() => nextTarget(full, 0));
  const lenRef = useRef(len);
  const prevRef = useRef(full);

  useEffect(() => {
    // A new utterance — the incoming text no longer extends what is being revealed. (Growth within
    // a turn always starts with what came before, so this is a precise turn boundary, not a guess.)
    const restarted = !full.startsWith(prevRef.current);
    if (restarted) lenRef.current = 0;
    prevRef.current = full;

    const tick = (force) => {
      const next = Math.max(lenRef.current, Math.min(full.length, nextTarget(full, lenRef.current)));
      if (force || next !== lenRef.current) {
        lenRef.current = next;
        setLen(next);
      }
    };

    tick(restarted);   // forced on a restart: `len` still holds the previous turn's larger cursor
    const id = setInterval(tick, tickMs);
    return () => clearInterval(id);
  }, [full, nextTarget, tickMs]);

  return full.slice(0, wordBoundary(full, len));
}

// How many characters of `full` the agent's voice has got through. `null` progress (no audio clock
// at all) means the whole thing — the pre-pacing behaviour and the safe default everywhere else.
function voicePacedTarget(full) {
  const progress = speechProgress();
  if (progress === null) return full.length;
  return Math.round(full.length * progress);
}

// Close half the remaining gap each tick, and at least one character so it can never stall short.
function smoothTarget(full, current) {
  return current + Math.ceil((full.length - current) * CATCH_UP);
}

// Cut back to the last completed word rather than mid-syllable — a caption that reveals "the micro-
// rot" while the voice is halfway through "micro-rotor" reads as a glitch, not as pacing.
function wordBoundary(text, len) {
  if (len >= text.length) return text.length;
  if (len <= 0) return 0;
  const space = text.lastIndexOf(' ', len);
  return space > 0 ? space : 0;
}
